/**
 * Itinerary Utilities
 *
 * Helper functions for handling itinerary data transformations,
 * particularly for backwards compatibility between old and new API structures.
 */

/**
 * Normalize itinerary response to handle both old and new API structures.
 *
 * Old structure: itinerary.itinerary_tour[]
 * New structure: itinerary.slots_itineraries[]
 *
 * @param {Object} itinerary - Raw itinerary from API
 * @returns {Object} Normalized itinerary with booking_items
 */
export function normalizeItineraryResponse(itinerary) {
  if (!itinerary) return null;

  // Get items from either new or old structure
  const items = itinerary.slots_itineraries || itinerary.itinerary_tour || [];

  // Normalize each item
  const bookingItems = items.map((item) => normalizeBookingItem(item));

  return {
    ...itinerary,
    commission_amount: Number(itinerary.commission_amount ?? 0),
    // Provide normalized booking_items for component use
    booking_items: bookingItems,
    // Keep backward compatibility
    slots_itineraries: bookingItems,
  };
}

/**
 * Normalize a single booking item to consistent structure.
 *
 * @param {Object} item - Raw booking item from API
 * @returns {Object} Normalized booking item
 */
export function normalizeBookingItem(item) {
  if (!item) return null;

  // Migrate legacy transportation fields to custom_field_values if needed
  let customFieldValues = item.custom_field_values || {};

  if (
    !item.custom_field_values ||
    Object.keys(item.custom_field_values).length === 0
  ) {
    // Check for legacy transportation fields and migrate them
    if (
      item.transportation_pickup_location ||
      item.transportation_pickup_time ||
      item.transportation_dropoff_location
    ) {
      customFieldValues = {
        pickup_location: item.transportation_pickup_location || "",
        pickup_time: item.transportation_pickup_time || "",
        dropoff_location: item.transportation_dropoff_location || "",
      };
    }
  }

  return {
    ...item,
    custom_field_values: customFieldValues,
  };
}

/**
 * Prepare booking items for API submission.
 * Transforms internal component data to new API structure.
 *
 * New API format expects (quantity_of_participants is per-item, not global):
 * - service_id: number
 * - start_datetime: string (ISO)
 * - end_datetime: string (ISO)
 * - quantity_of_participants: number (REQUIRED - per booking item)
 * - custom_field_values: object (optional)
 *
 * @param {Array} items - Array of internal booking item objects
 * @returns {Array} Array of booking items ready for API
 */
export function prepareBookingItemsForApi(items) {
  if (!items || !Array.isArray(items)) return [];

  return items.map((item) => {
    // Extract date and time from start_datetime if needed
    let date = item.date;
    let time = item.time;

    if (!date && item.start_datetime) {
      // Handle both ISO string and datetime-local formats
      const dateTimeParts = item.start_datetime.split('T');
      date = dateTimeParts[0];
      if (!time && dateTimeParts[1]) {
        time = dateTimeParts[1].substring(0, 5); // Get HH:mm
      }
    }

    return {
      service_id: item.service_id,
      date: date,
      time: time,
      quantity_of_participants: item.quantity_of_participants || 1,
      // Also include full datetime for backwards compatibility
      start_datetime: item.start_datetime,
      end_datetime: item.end_datetime,
      service_slot_id: item.service_slot_id || null,
      custom_field_values: item.custom_field_values || {},
    };
  });
}

/**
 * Get custom field label from schema.
 * Falls back to formatting the key if not found.
 *
 * @param {Array} schema - Custom fields schema array
 * @param {string} key - Field key to look up
 * @returns {string} Human-readable label
 */
export function getFieldLabelFromSchema(schema, key) {
  if (!schema || !Array.isArray(schema)) {
    return formatFieldKey(key);
  }

  const field = schema.find((f) => f.key === key);
  return field?.label || formatFieldKey(key);
}

/**
 * Format a field key to human-readable label.
 * Converts snake_case or camelCase to Title Case.
 *
 * @param {string} key - Field key
 * @returns {string} Formatted label
 */
export function formatFieldKey(key) {
  if (!key) return "";

  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format custom field value for display.
 *
 * @param {*} value - Field value
 * @param {Object} fieldSchema - Optional field schema for type information
 * @returns {string} Formatted value
 */
export function formatFieldValue(value, fieldSchema = null) {
  if (value === null || value === undefined) return "";

  if (typeof value === "boolean") {
    return value ? "Sí" : "No";
  }

  if (fieldSchema?.type === "date" || value instanceof Date) {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return String(value);
    }
  }

  if (fieldSchema?.type === "time") {
    return value;
  }

  return String(value);
}

/**
 * Validate custom field values against schema.
 *
 * @param {Object} values - Custom field values object
 * @param {Array} schema - Custom fields schema
 * @returns {Object} { valid: boolean, errors: Object }
 */
export function validateCustomFieldValues(values, schema) {
  const errors = {};

  if (!schema || !Array.isArray(schema)) {
    return { valid: true, errors: {} };
  }

  for (const field of schema) {
    const value = values?.[field.key];

    // Check required fields
    if (field.required) {
      if (value === undefined || value === null || value === "") {
        errors[field.key] = `${field.label || formatFieldKey(field.key)} es requerido`;
        continue;
      }
    }

    // Type-specific validation
    if (value !== undefined && value !== null && value !== "") {
      if (field.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.key] = `${field.label || formatFieldKey(field.key)} debe ser un correo válido`;
        }
      }

      if (field.type === "number") {
        const numValue = Number(value);
        if (field.min !== undefined && numValue < field.min) {
          errors[field.key] = `${field.label || formatFieldKey(field.key)} debe ser mayor o igual a ${field.min}`;
        }
        if (field.max !== undefined && numValue > field.max) {
          errors[field.key] = `${field.label || formatFieldKey(field.key)} debe ser menor o igual a ${field.max}`;
        }
      }
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Check if any booking items have a specific service type.
 *
 * @param {Array} items - Booking items
 * @param {string} serviceTypeSlug - Service type slug to check for
 * @returns {boolean}
 */
export function hasServiceTypeInItems(items, serviceTypeSlug) {
  if (!items || !Array.isArray(items)) return false;

  return items.some(
    (item) => item.service?.service_type?.slug === serviceTypeSlug
  );
}

export default {
  normalizeItineraryResponse,
  normalizeBookingItem,
  prepareBookingItemsForApi,
  getFieldLabelFromSchema,
  formatFieldKey,
  formatFieldValue,
  validateCustomFieldValues,
  hasServiceTypeInItems,
};
